import { computed, ref } from 'vue';
import jwtDecode from 'jwt-decode';
// import formatISO9075 from 'date-fns/formatISO9075';
import parseISO from 'date-fns/parseISO';
import {
  DEFAULT_DATETIME,
  Evento,
  Motorista,
  Veiculo,
  Motivo,
  Cliente,
  Fop,
  TipoDeDespesa,
  Fornecedor,
  Despesa,
  Atendimento
} from 'src/api/schema';
import { useLocalforage, reading } from 'src/localforage';

export const version = import.meta.env.PACKAGE_VERSION || '0';
// console.log('[version]', version);
// export const focused = ref<boolean>(true);
export const visible = ref<boolean>(document.visibilityState === 'visible');
export const connected = ref<boolean>(window.navigator.onLine);
export const synchronizing = ref<boolean>(false);

export const [accessToken, setAccessToken] = useLocalforage<
  undefined | null | string
>('accessToken', undefined, null);
export const [motoristas, setMotoristas] = useLocalforage<Motorista[]>(
  'motoristas',
  []
);
export const [veiculos, setVeiculos] = useLocalforage<Veiculo[]>(
  'veiculos',
  []
);
export const [fornecedores, setFornecedores] = useLocalforage<Fornecedor[]>(
  'fornecedores',
  []
);
export const [motivos, setMotivos] = useLocalforage<Motivo[]>('motivos', []);
export const [clientes, setClientes] = useLocalforage<Cliente[]>(
  'clientes',
  []
);
export const [fops, setFops] = useLocalforage<Fop[]>('fops', []);
export const [foas, setFoas] = useLocalforage<Fop[]>('foas', []);
export const [tiposDeDespesa, setTiposDeDespesa] = useLocalforage<
  TipoDeDespesa[]
>('tiposDeDespesa', []);
export const [eventos, setEventos] = useLocalforage<Evento[]>('eventos', []);
export const [eventosToSync, setEventosToSync] = useLocalforage<Evento[]>(
  'eventosToSync',
  []
);
export const [despesas, setDespesas] = useLocalforage<Despesa[]>(
  'despesas',
  []
);
export const [atendimentos, setAtendimentos] = useLocalforage<Atendimento[]>(
  'atendimentos',
  []
);
export const [despesasToSync, setDespesasToSync] = useLocalforage<
  Despesa[]
>('despesasToSync', []);
export const [atendimentosToSync, setAtendimentosToSync] = useLocalforage<
  Atendimento[]
>('atendimentosToSync', []);
export const [fornecedoresToSync, setFornecedoresToSync] = useLocalforage<
  Fornecedor[]
>('fornecedoresToSync', []);

//eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-ignore
window.setAccessToken = (val: string) => setAccessToken(val);

export const todosOsEventos = computed(() => {
  const arr: Evento[] = [
    ...eventos.value.filter((e) => !e.dat || e.dat === DEFAULT_DATETIME),
  ];
  for (const ets of eventosToSync.value) {
    const i = arr.findIndex((e) => e.uid === ets.uid);
    if (i >= 0) {
      arr[i] = ets;
    } else {
      arr.push(ets);
    }
  }
  return arr.sort(
    (a, b) => parseISO(a.inicio).getTime() - parseISO(b.inicio).getTime()
  );
});

export interface JwtObject {
  uid: number;
  eid: number;
  exp: number;
  rules?: string[];
}

export const loggedIn = computed<null | JwtObject>(() =>
  accessToken.value ? jwtDecode<JwtObject>(accessToken.value) : null
);
export const needSync = computed<boolean>(() => eventosToSync.value.length > 0);

export const motoristasMat = computed<null | Date>(() => {
  let mat = null;
  for (const m of motoristas.value) {
    const x = parseISO(m.mat);
    if (mat === null || x > mat) mat = x;
  }
  return mat;
});

export const despesasMat = computed<null | Date>(() => {
  let mat = null;
  for (const m of despesas.value) {
    const x = parseISO(m.mat);
    if (mat === null || x > mat) mat = x;
  }
  return mat;
});

export const atendimentosMat = computed<null | Date>(() => {
  let mat = null;
  for (const m of atendimentos.value) {
    const x = parseISO(m.mat);
    if (mat === null || x > mat) mat = x;
  }
  return mat;
});

export const veiculosMat = computed<null | Date>(() => {
  let mat = null;
  for (const m of veiculos.value) {
    const x = parseISO(m.mat);
    if (mat === null || x > mat) mat = x;
  }
  return mat;
});

export const fornecedoresMat = computed<null | Date>(() => {
  let mat = null;
  for (const m of fornecedores.value) {
    const x = parseISO(m.mat);
    if (mat === null || x > mat) mat = x;
  }
  return mat;
});

export const motivosMat = computed<null | Date>(() => {
  let mat = null;
  for (const m of motivos.value) {
    const x = parseISO(m.mat);
    if (mat === null || x > mat) mat = x;
  }
  return mat;
});

export const fopsMat = computed<null | Date>(() => {
  let mat = null;
  for (const m of fops.value) {
    const x = parseISO(m.mat);
    if (mat === null || x > mat) mat = x;
  }
  return mat;
});

export const foasMat = computed<null | Date>(() => {
  let mat = null;
  for (const m of foas.value) {
    const x = parseISO(m.mat);
    if (mat === null || x > mat) mat = x;
  }
  return mat;
});

export const tiposDeDespesaMat = computed<null | Date>(() => {
  let mat = null;
  for (const m of tiposDeDespesa.value) {
    const x = parseISO(m.mat);
    if (mat === null || x > mat) mat = x;
  }
  return mat;
});

export const clientesMat = computed<null | Date>(() => {
  let mat = null;
  for (const m of clientes.value) {
    const x = parseISO(m.mat);
    if (mat === null || x > mat) mat = x;
  }
  return mat;
});

export const eventosMat = computed<null | Date>(() => {
  let mat = null;
  for (const m of eventos.value) {
    const x = parseISO(m.mat);
    if (mat === null || x > mat) mat = x;
  }
  return mat;
});

export const motorista = computed(
  () => motoristas.value.find((m) => m.id === loggedIn.value?.uid) ?? null
);

export const appIsLoading = computed<boolean>(() => {
  if (reading.value > 0) return true;
  if (accessToken.value === undefined) return true;
  if (
    accessToken.value &&
    !motorista.value &&
    (synchronizing.value || motoristas.value.length === 0)
  ) {
    return true;
  }
  return false;
});

export const ultimoEvento = computed<null | Evento>(() =>
  todosOsEventos.value.length > 0
    ? todosOsEventos.value[todosOsEventos.value.length - 1]
    : null
);

export const todasAsDespesas = computed<Despesa[]>(() => [
  ...despesas.value,
  ...despesasToSync.value,
]);

export const todosOsAtendimentos = computed<Atendimento[]>(() => [
  ...atendimentos.value,
  ...atendimentosToSync.value,
]);

export const todosOsFornecedores = computed<Fornecedor[]>(() => [
  ...fornecedores.value,
  ...fornecedoresToSync.value,
]);

// export const todosOsClientes = computed<Fornecedor[]>(() => [
//   ...clientes.value,
//   ...fornecedoresToSync.value,
// ]);

// watch(loggedIn, function (id) {
//   gtag('config', 'GA_MEASUREMENT_ID', {
//     user_id: id ?? null,
//   });
// });

// window.addEventListener('blur', () => focused.value = false);
// window.addEventListener('focus', () => focused.value = true);

document.addEventListener(
  'visibilitychange',
  () => (visible.value = document.visibilityState === 'visible')
);

window.addEventListener('offline', () => (connected.value = false));
window.addEventListener('online', () => (connected.value = true));
